// Core
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Styles
import './styles.scss';
import { useSelector } from 'react-redux';
import { fetchData } from 'utils/fetch';
import { restAPIs } from 'utils/restAPIs';
import { getStoredToken } from 'utils/auth';

import Button from 'components/commonComponents/Button';

import CryptoJS from 'crypto-js';
import * as moment from 'moment-timezone';
import { Alert, Spin } from 'antd';
import { ArrowRightOutlined, CreditCardOutlined, MobileOutlined } from '@ant-design/icons';
import { APP_ID } from 'config/constants';
import { Fade } from 'react-reveal';

const GATEWAY_URL = 'https://www.ipg-online.com/connect/gateway/processing';
// SECURITY: this secret signs gateway requests and must not live in the browser.
// Anyone can read it from the bundle and sign a request with any chargetotal.
// Move signing to the backend (see handoff notes); kept here only until then.
const GATEWAY_SHARED_SECRET = 'Qe46L?+hdm';

const SubscribePayment = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLang } = useSelector((store) => store.commonData);
  const [data, setData] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [selectedType, setType] = useState(0);
  const { subId } = useParams();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('refcode');
  const discountCode = searchParams.get('discountCode');

  const [subs, setSubs] = useState(null);
  const [recurringInterval, setRecurringInterval] = useState('');
  const [orderId, setOrderId] = useState('');
  const [orderError, setOrderError] = useState('');
  const [isCreatingOrder, setCreatingOrder] = useState(false);

  const fetchSubscriptions = async () => {
    const formData = new FormData();
    formData.append('language', currentLang?.id);
    formData.append('appId', APP_ID);
    if (referralCode) formData.append('refCode', referralCode);
    if (discountCode) formData.append('discountCode', discountCode);
    const res = await fetchData(restAPIs.getSubscriptions(formData));
    const plan = Array.isArray(res?.info)
      ? res.info.find((item) => String(item.subscriptionId) === String(subId))
      : null;
    setSubs(plan || null);
    setRecurringInterval(plan ? (plan.duration === 30 ? 'month' : 'year') : '');
    return plan;
  };

  const fetchPaymentMethods = async () => {
    const formData = new FormData();
    formData.append('language', currentLang?.id);
    formData.append('appId', APP_ID);
    const res = await fetchData(restAPIs.subscriptionForm(formData));
    setData(Array.isArray(res?.paymentMethods) ? res.paymentMethods : []);
  };

  // One order per plan visit. Re-running this on a language switch used to
  // create a duplicate order each time.
  const createOrder = async () => {
    setCreatingOrder(true);
    setOrderError('');
    setOrderId('');
    const formData = new FormData();
    formData.append('appId', APP_ID);
    formData.append('action', 'orderNow');
    formData.append('subscriptionId', subId);
    formData.append('paymentMethodId', '1');
    const res = await fetchData(restAPIs.subscriptionForm(formData));
    if (res?.statusCode === 200 && res?.orderId) {
      setOrderId(res.orderId);
    } else {
      setOrderError(res?.errormessage || 'Unable to start the payment. Please try again.');
    }
    setCreatingOrder(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    Promise.all([fetchSubscriptions(), fetchPaymentMethods()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang, subId, referralCode, discountCode]);

  useEffect(() => {
    createOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subId]);

  const callbackURL = orderId
    ? 'https://alwaraq.net/payment/orderSubComplete.php?returnUrl=https://cinemapedia.net/&orderId=' +
      orderId + '&sessiontoken=' + getStoredToken() + '&appId=' + APP_ID + '&transactionStatus='
    : '';

  const canPayByCard = Boolean(orderId && subs?.price && !isCreatingOrder);

  // Sign synchronously inside the real submit event, so the gateway never
  // receives an unsigned or stale request.
  const handleCardSubmit = (event) => {
    const form = event.currentTarget;
    if (!canPayByCard) {
      event.preventDefault();
      return;
    }
    form.elements.txndatetime.value = moment().tz('Asia/Dubai').format('YYYY:MM:DD-HH:mm:ss');
    const params = {};
    new FormData(form).forEach((value, key) => {
      if (key !== 'hashExtended' && value !== '') params[key] = value;
    });
    const content = Object.keys(params).sort().map((key) => params[key]).join('|');
    const signature = CryptoJS.HmacSHA256(content, GATEWAY_SHARED_SECRET);
    form.elements.hashExtended.value = CryptoJS.enc.Base64.stringify(signature);
  };

  const handlePaymentmode = () => {
    if (referralCode) navigate('/etisalat/' + subId + '?refcode=' + referralCode);
    else if (discountCode) navigate('/etisalat/' + subId + '?discountCode=' + discountCode);
    else navigate('/etisalat/' + subId);
  };

  if (!isLoading && subs === null) {
    return (
      <div className='container __innerTopPad text-center'>
        <h1 className='__common-head'>{t('subscription')}</h1>
        <Alert type='error' showIcon message='This subscription plan is not available.' />
        <Button label={t('subscribe')} className='mt-4' handleOnClick={() => navigate('/subscribe')} />
      </div>
    );
  }

  return (
    <>
      <div className='container __innerTopPad'>
        <h1 className='__common-head'>{t('subscription')}</h1>
        {isLoading ?
          <div className="row justify-content-center">
            <div className='text-center'>
              <Spin className='__spin_color' size="large" tip="Loading" />
            </div>
          </div> :
          <>
            <div className="row justify-content-center">
              {data && data.map((item, idx) => {
                return (
                  <Fade left cascade key={item?.paymentMethodId ?? idx}>
                    <div className="col-md-5 mb-4">
                      <div className="__sub_p_Block" onClick={() => setType(idx)} style={{ border: selectedType === idx ? '2px solid #e3a83a' : '' }}>
                        {selectedType === idx ? <span className='recommend'>SELECTED</span> : ''}
                        {idx === 0 ? <CreditCardOutlined style={{ fontSize: 60, marginBottom: '20px' }} color="secondary" /> : <MobileOutlined style={{ fontSize: 60, marginBottom: '20px' }} color="secondary" />}
                        <h1>{item?.name}</h1>
                        <h2>{item?.description}</h2>
                      </div>
                    </div>
                  </Fade>
                )
              })}
            </div>
            {
              selectedType === 0 ?
                <div>
                  {orderError ?
                    <div className='mb-3'>
                      <Alert type='error' showIcon message={orderError} />
                      <Button label='Retry' className='mt-2' handleOnClick={createOrder} />
                    </div> : ''}
                  <form id="paymentForm" method="post" action={GATEWAY_URL} onSubmit={handleCardSubmit}>
                    <input type="hidden" name="hash_algorithm" value="HMACSHA256" />
                    <input type="hidden" name="checkoutoption" value="combinedpage" />
                    <input type="hidden" name="language" value="en_US" />
                    <input type="hidden" name="hashExtended" defaultValue="" />
                    <input type="hidden" name="storename" value="811181815" />
                    <fieldset style={{ display: 'none' }}>
                      <select name="full_bypass" defaultValue="false">
                        <option value="false">false</option>
                        <option value="true">true</option>
                      </select>
                      <select name="paymentMethod" defaultValue="">
                        <option value="">-</option>
                      </select>
                      <select name="timezone" defaultValue="Asia/Dubai">
                        <option value="Asia/Dubai">Asia/Dubai</option>
                        <option value="Europe/Berlin">Europe/Berlin</option>
                      </select>
                      <input type="text" name="txndatetime" defaultValue="" />
                      <select name="txntype" defaultValue="sale">
                        <option value="sale">Sale</option>
                      </select>
                      <input type="text" name="chargetotal" value={subs?.price ?? ''} readOnly />
                      <select name="authenticateTransaction" defaultValue="true">
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                      <select name="currency" defaultValue="784">
                        <option value="784">AED (784)</option>
                      </select>
                      <input type="text" name="referencedSchemeTransactionId" defaultValue="" />
                      <select name="unscheduledCredentialOnFileType" defaultValue="">
                        <option value=""></option>
                        <option value="FIRST">FIRST</option>
                        <option value="CARDHOLDER_INITIATED">CARDHOLDER_INITIATED</option>
                        <option value="MERCHANT_INITIATED">MERCHANT_INITIATED</option>
                      </select>
                      <input type="text" name="responseFailURL" size="60" value={callbackURL + '0'} readOnly />
                      <input type="text" name="responseSuccessURL" size="60" value={callbackURL + '1'} readOnly />
                      <select name="assignToken" defaultValue="false">
                        <option value="false">false</option>
                        <option value="true">true</option>
                      </select>
                      <input type="text" name="hosteddataid" size="60" defaultValue="" />
                      <input type="text" name="recurringInstallmentCount" defaultValue="999" />
                      <input type="text" name="recurringInstallmentPeriod" value={recurringInterval} readOnly />
                      <input type="text" name="recurringInstallmentFrequency" defaultValue="1" />
                      <input type="text" name="recurringComments" defaultValue="Any comments about the recurring transaction" />
                    </fieldset>
                    <Button
                      id="submit"
                      label='Continue'
                      icon={<ArrowRightOutlined />}
                      className='mt-4'
                      type="submit"
                      withLoader
                      loading={isCreatingOrder}
                      disabled={!canPayByCard}
                    />
                  </form>
                </div>
                : <Button
                  label='Continue'
                  icon={<ArrowRightOutlined />}
                  className='mt-4'
                  handleOnClick={handlePaymentmode}
                />
            }
          </>
        }
      </div>
    </>
  );
};

export default SubscribePayment;
